<template>
<div>
  <div class="row">
    <!-- Header Section - Bulletin Status Only -->
    <div class="col-md-12 mb-3" v-if="selectedClasse && selectedTrimester">
      <div class="bulletin-header d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-3">
          <span class="bulletin-status-title">
            {{ $t("bulletin.parentVisibility") }} : 
          </span>
          <!-- Bulletin Status Indicator -->
          <div class="bulletin-status-badge">
            <a-icon
              :type="isValidatedBulletin() ? 'check-circle' : 'close-circle'"
              :class="[
                'bulletin-status-icon',
                isValidatedBulletin() ? 'success' : 'error',
              ]"
            />
            <span>
              {{
                isValidatedBulletin()
                  ? $t("bulletin.bulletinDispo")
                  : $t("bulletin.bulletinNotDispo")
              }}
            </span>
          </div>
        </div>
        <a-tooltip placement="bottom">
          <template slot="title">
            {{
              isValidatedBulletin()
                ? $t("bulletin.hideBulletin")
                : $t("bulletin.validateBulletin")
            }}
          </template>
          <a-button
            :type="isValidatedBulletin() ? 'danger' : 'primary'"
            :ghost="isValidatedBulletin()"
            :loading="validatingBulletin"
            @click="confirmeBulletinValidation()"
            class="bulletin-action-btn"
          >
            <a-icon :type="isValidatedBulletin() ? 'eye-invisible' : 'eye'" />
            {{
              isValidatedBulletin()
                ? $t("bulletin.cancelValidation")
                : $t("bulletin.validatedClassroom")
            }}
          </a-button>
        </a-tooltip>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="col-md-12 mb-2">
      <label>{{ $t("bulletin.listeClasses") }}</label>
      <a-select
        show-search
        :value="selectedClasse || undefined"
        :placeholder="$t('emploi.listeClasses')"
        option-filter-prop="children"
        :filter-option="filterOption"
        :loading="classesLoading"
        :disabled="classesLoading"
        class="select-class ml-3"
        @change="handleClassChange"
      >
        <a-select-option
          v-for="classe in classes"
          :key="classe._id"
          :value="classe._id"
        >
          {{ classe.name }}
        </a-select-option>
      </a-select>

      <label class="label-spacing">{{ $t("menu.eleves") }}</label>
      <a-select
        show-search
        option-filter-prop="children"
        :filter-option="filterOption"
        :placeholder="$t('menu.eleves')"
        class="select-student ml-3"
        :loading="studentLoading"
        :disabled="studentLoading"
        :value="selectedStudent || undefined"
        @change="handleStudentChange"
      >
        <a-select-option
          v-for="student in activeData"
          :key="student._id"
          :value="student._id"
        >
          {{ student.fullName }}
        </a-select-option>
      </a-select>

      <label class="label-spacing">{{ $t("emploi.trimestre") }}</label>
      <a-select
        :placeholder="$t('paiement.selectionnerMois')"
        class="select-trimester ml-3"
        :value="selectedTrimester || '1'"
        @change="handleTrimesterChange"
      >
        <a-select-option
          v-for="trimester in ['1', '2', '3']"
          :key="trimester"
          :value="trimester"
        >
          {{ trimester }}
        </a-select-option>
      </a-select>
      <a-dropdown
        v-show="selectedClasse && selectedTrimester"
        class="bulletin-print-btn ml-3"
      >
        <template #overlay>
          <a-menu @click="(val) => handlePrintMenuClick(val.key)">
            <a-menu-item-group key="bulletin-print" :title="$t('bulletin.bulletins')">
              <a-menu-item key="print-single-tn" v-if="selectedStudent">
                <img
                  style="border-radius: 25%; margin-right: 5px"
                  width="20"
                  src="resources/images/flags/tunisia.svg"
                  alt="TN"
                />
                {{ $t("bulletin.imprimerBulletin") }}
              </a-menu-item>
              <a-menu-item key="print-all-tn">
                <img
                  style="border-radius: 25%; margin-right: 5px"
                  width="20"
                  src="resources/images/flags/tunisia.svg"
                  alt="TN"
                />
                {{ $t("bulletin.imprimerTout") }}
              </a-menu-item>
              <a-menu-item key="print-single-fr" v-if="selectedStudent">
                <img
                  style="border-radius: 25%; margin-right: 5px"
                  width="20"
                  src="resources/images/flags/france.svg"
                  alt="FR"
                />
                {{ $t("bulletin.imprimerBulletin") }}
              </a-menu-item>
              <a-menu-item key="print-all-fr">
                <img
                  style="border-radius: 25%; margin-right: 5px"
                  width="20"
                  src="resources/images/flags/france.svg"
                  alt="FR"
                />
                {{ $t("bulletin.imprimerTout") }}
              </a-menu-item>
            </a-menu-item-group>
            <a-menu-item-group key="pv-print" :title="$t('bulletin.pv')">
              <a-menu-item key="print-pv">
                {{ $t("bulletin.imprimerPV") }}
              </a-menu-item>
            </a-menu-item-group>
          </a-menu>
        </template>
        <a-button
          :loading="printLoading"
          :disabled="!selectedClasse || !selectedTrimester"
          type="default"
          icon="printer"
        >
          {{ $t("bulletin.imprimerBulletin") || "Imprimer" }}
        </a-button>
      </a-dropdown>
    </div>
    <div class="col-xl-12">
      <div
        class="card mb-2"
        v-if="selectedStudent && selectedClasse && currentLevel"
      >
        <a-table
          :columns="columns"
          :data-source="subjectList"
          :pagination="false"
          :loading="tableLoading"
          :scroll="{ x: 'max-content' }"
        >
          <template slot="name" slot-scope="text, record">
            <strong>{{ record.name }}</strong>
          </template>
          <template
            v-for="markTypeKey in markTypesKeys"
            :slot="markTypeKey"
            slot-scope="text, record"
          >
            <div
              v-if="
                record.markTypes &&
                record.markTypes[markTypeKey] &&
                record.markTypes[markTypeKey].enabled
              "
              :key="`${markTypeKey}-${record._id}`"
              class="mark-type-column"
            >
              <div class="mark-type-header-cell">
                <span v-if="record.markTypes[markTypeKey].count > 1">
                  (×{{ record.markTypes[markTypeKey].count }})
                </span>
                <span
                  v-if="record.markTypes[markTypeKey].subsubject"
                  class="subsubject-label"
                >
                  {{ getSubsubjectName(record.markTypes[markTypeKey].subsubject) }}
                </span>
              </div>
              <div
                v-if="
                  isExempted(record._id, markTypeKey, selectedStudent)
                "
                class="exempted-notice-cell"
              >
                <a-alert
                  :message="$t('bulletin.alreadyExempt')"
                  type="warning"
                  banner
                  closable
                  @close="
                    () =>
                      exemptMarkType(
                        record._id,
                        markTypeKey,
                        selectedStudent,
                        false
                      )
                  "
                />
              </div>
              <div v-else class="mark-inputs-cell">
                <div
                  v-for="(markValue, index) in getMarksArray(
                    record._id,
                    markTypeKey,
                    selectedStudent,
                    record.markTypes[markTypeKey].count
                  )"
                  :key="index"
                  class="mark-input-row"
                  :class="markValue < 0 || markValue > 20 ? 'danger' : ''"
                >
                  <span
                    v-if="record.markTypes[markTypeKey].count > 1"
                    class="mark-index"
                  >
                    #{{ index + 1 }}
                  </span>
                  <a-input-number
                    :value="markValue"
                    class="mark-input-number"
                    :min="0"
                    :max="20"
                    @change="
                      (val) => {
                        updateMarkAtIndex(
                          val,
                          record._id,
                          markTypeKey,
                          index,
                          selectedStudent,
                          record.markTypes[markTypeKey]
                        );
                      }
                    "
                  ></a-input-number>
                  <a-popconfirm
                    :title="$t('bulletin.confirmExemptStudent')"
                    @confirm="
                      () =>
                        exemptMarkType(
                          record._id,
                          markTypeKey,
                          selectedStudent,
                          true
                        )
                    "
                    v-if="
                      !isExempted(
                        record._id,
                        markTypeKey,
                        selectedStudent
                      )
                    "
                  >
                    <a-icon
                      type="stop"
                      class="exempt-icon"
                      :title="$t('bulletin.exempt')"
                    />
                  </a-popconfirm>
                  <a-popconfirm
                    :title="$t('bulletin.confirmUnexemptStudent')"
                    @confirm="
                      () =>
                        exemptMarkType(
                          record._id,
                          markTypeKey,
                          selectedStudent,
                          false
                        )
                    "
                    v-else
                  >
                    <a-icon
                      type="undo"
                      class="exempt-icon"
                      :title="$t('bulletin.alreadyExempt')"
                    />
                  </a-popconfirm>
                </div>
              </div>
            </div>
          </template>
          <template slot="moyenne" slot-scope="text, record">
            <strong>{{ calculateMoyenne(record._id) }}</strong>
          </template>
        </a-table>
      </div>
      <div
        v-if="hasUnsavedChanges && selectedClasse && selectedStudent"
        class="row my-2"
      >
        <div class="col-xl-12">
          <a-alert
            :message="$t('all.unsavedChangesInfo')"
            type="warning"
            show-icon
            closable
            @close="() => {}"
          />
        </div>
      </div>
      <div
        class="row"
        v-if="selectedClasse && selectedStudent && activeData.length !== 0"
      >
        <div class="col-xl-7">{{ $t("bulletin.toutMatieres") }}</div>
        <div class="col-xl-3">
          <a-button
            :loading="saveLoading"
            class="save-all-button"
            type="primary"
            @click="saveAll()"
          >
            {{ $t("bulletin.enregistrerTout") }}<a-icon type="check" />
          </a-button>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import marksCommon from "../mixins/marksCommon";
import printBulletin from "../mixins/printBulletin";
import printPvCollege from "../mixins/printPvCollege";
import "../mixins/marksCommon.css";
import "@/views/parametrage/bulletin/components/bulletinBuilder/bulletinBuilder.css";

export default {
  name: "ByStudent",
  mixins: [marksCommon, printBulletin, printPvCollege],
  computed: {
    currentLevel() {
      return this.getCurrentLevel(this.selectedClasse);
    },
    columns() {
      const cols = [
        {
          title: this.$t("bulletin.matieres"),
          dataIndex: "name",
          key: "name",
          fixed: "left",
          width: 150,
        },
      ];

      // Add a column for each markType that is enabled in at least one subject
      const enabledMarkTypes = new Set();
      this.subjectList.forEach((subject) => {
        Object.keys(subject.markTypes || {}).forEach((markTypeKey) => {
          if (subject.markTypes[markTypeKey]?.enabled) {
            enabledMarkTypes.add(markTypeKey);
          }
        });
      });

      // Add columns for enabled markTypes, but put "synth" last
      const otherMarkTypes = this.markTypesKeys.filter(key => key !== 'synth');
      const synthMarkType = 'synth';
      
      // Add other markTypes first
      otherMarkTypes.forEach((markTypeKey) => {
        if (enabledMarkTypes.has(markTypeKey)) {
          cols.push({
            title: this.$t(`college.bulletin.config.markType.${markTypeKey}`),
            key: markTypeKey,
            scopedSlots: { customRender: markTypeKey },
          });
        }
      });
      
      // Add "synth" last if enabled
      if (enabledMarkTypes.has(synthMarkType)) {
        cols.push({
          title: this.$t(`college.bulletin.config.markType.${synthMarkType}`),
          key: synthMarkType,
          scopedSlots: { customRender: synthMarkType },
        });
      }

      // Add "Moyenne" column at the end
      cols.push({
        title: () => {
          const h = this.$createElement;
          return h('strong', this.$t("bulletin.moyenne"));
        },
        key: "moyenne",
        scopedSlots: { customRender: "moyenne" },
        fixed: "right",
        width: 100,
      });

      return cols;
    },
  },
  async created() {
    await this.loadClasses();
  },
  data() {
    return {
      selectedClasse: null,
      selectedStudent: null,
      selectedTrimester: "1",
      activeData: [],
      subjectList: [],
      markData: [],
      originalMarkData: [],
      tableLoading: false,
      studentLoading: false,
      saveLoading: false,
      hasUnsavedChanges: false,
      validatingBulletin: false,
    };
  },
  mounted() {
    // Prevent leaving page if there are unsaved changes
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    // Notify parent of initial state
    this.$emit("unsaved-changes", this.hasUnsavedChanges);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  methods: {
    async handleClassChange(value) {
      this.selectedClasse = value;
      this.selectedStudent = null;
      this.subjectList = [];
      this.markData = [];
      this.resetUnsavedChanges();
      if (value) {
        await this.loadStudentsForClass();
        await this.loadConfigForClass();
      }
    },
    async handleTrimesterChange(value) {
      const confirmed = await this.checkUnsavedChanges();
      if (!confirmed) return;

      this.selectedTrimester = value;
      this.resetUnsavedChanges();
      if (this.selectedClasse && this.currentLevel) {
        await this.loadConfigForClass();
        if (this.selectedStudent) {
          await this.updateMarks();
        }
      }
    },
    async loadStudentsForClass() {
      if (!this.selectedClasse) return;
      this.studentLoading = true;
      try {
        this.activeData = await this.loadStudents(this.selectedClasse);
      } finally {
        this.studentLoading = false;
      }
    },
    async loadConfigForClass() {
      if (!this.selectedClasse || !this.currentLevel) return;
      this.tableLoading = true;
      try {
        this.subjectList = await this.loadConfig(
          this.currentLevel,
          this.selectedTrimester
        );
      } finally {
        this.tableLoading = false;
      }
    },
    async handleStudentChange(value) {
      const confirmed = await this.checkUnsavedChanges();
      if (!confirmed) return;

      this.selectedStudent = value;
      if (value) {
        await this.updateMarks();
      } else {
        this.markData = [];
        this.originalMarkData = [];
        this.resetUnsavedChanges();
      }
    },
    async updateMarks() {
      if (!this.selectedStudent || !this.selectedClasse || !this.currentLevel)
        return;
      this.tableLoading = true;
      try {
        const res = await apiClient.post("/bulletin/college/mark/filter", {
          query: {
            classroom: this.selectedClasse,
            student: this.selectedStudent,
            trimester: parseInt(this.selectedTrimester),
          },
        });

        this.markData = [];
        this.originalMarkData = res.data || [];

        (res.data || []).forEach((markDoc) => {
          const subjectId = markDoc.subject._id || markDoc.subject;
          const subject = this.subjectList.find(s => s._id === subjectId);
          
          const normalizedMarks = this.normalizeAllMarks(markDoc, subject);
          
          this.markData.push({
            _id: markDoc._id,
            student: markDoc.student._id || markDoc.student,
            subject: subjectId,
            marks: normalizedMarks,
            exemptions: markDoc.exemptions || {
              oral: false,
              cont1: false,
              cont2: false,
              synth: false,
              written: false,
              tp: false,
            },
            subsubject: markDoc.subsubject
              ? markDoc.subsubject._id || markDoc.subsubject
              : null,
          });
        });
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.tableLoading = false;
      }
    },
    getMarksArray(subjectId, markTypeKey, studentId, count) {
      return this.getMarksArrayGeneric(studentId, markTypeKey, count, subjectId);
    },
    isExempted(subjectId, markTypeKey, studentId) {
      return this.isExemptedGeneric(studentId, markTypeKey, subjectId);
    },
    calculateMoyenne(subjectId) {
      return this.calculateMoyenneGeneric('byStudent', subjectId);
    },
    updateMarkAtIndex(
      newMark,
      subjectId,
      markTypeKey,
      index,
      studentId,
      markTypeConfig
    ) {
      this.updateMarkAtIndexGeneric(
        newMark,
        studentId,
        markTypeKey,
        index,
        markTypeConfig,
        subjectId
      );
    },
    hasMarksChanged() {
      if (!this.markData || !this.originalMarkData || this.markData.length === 0) {
        return false;
      }

      // Compare current marks with original marks
      for (const currentMark of this.markData) {
        const originalMark = this.originalMarkData.find(
          (om) =>
            String(om.student._id || om.student) === String(currentMark.student) &&
            String(om.subject._id || om.subject) === String(currentMark.subject)
        );

        if (!originalMark) {
          // New mark added
          const hasAnyMark = Object.keys(currentMark.marks || {}).some((key) => {
            const marksArray = currentMark.marks[key];
            return Array.isArray(marksArray) && marksArray.some((m) => m !== null && m !== undefined);
          });
          if (hasAnyMark) return true;
          continue;
        }

        // Compare marks arrays
        for (const markTypeKey of this.markTypesKeys) {
          const currentMarks = currentMark.marks?.[markTypeKey] || [];
          const originalMarks = this.normalizeAllMarks(originalMark, this.subjectList.find(s => s._id === currentMark.subject))?.[markTypeKey] || [];

          if (currentMarks.length !== originalMarks.length) return true;

          for (let i = 0; i < currentMarks.length; i++) {
            if (currentMarks[i] !== originalMarks[i]) {
              return true;
            }
          }
        }
      }

      return false;
    },
    async exemptMarkType(subjectId, markTypeKey, studentId, exempted) {
      await this.exemptMarkTypeGeneric(studentId, markTypeKey, exempted, subjectId);
    },
    async saveAll() {
      await this.saveAllGeneric((subjectId) => {
        return this.subjectList.find(s => s._id === subjectId);
      });
    },
    async handlePrint() {
      if (!this.selectedStudent || !this.selectedTrimester) {
        this.$message.warning(
          this.$t("bulletin.selectStudentAndTrimester") ||
            "Please select a student and trimester"
        );
        return;
      }
      await this.printStudentBulletin(
        this.selectedStudent,
        this.selectedTrimester
      );
    },
    async handlePrintMenuClick(key) {
      if (key === "print-pv") {
        await this.printClassPv(this.selectedTrimester, "ar");
        return;
      }
      // French PV disabled for now
      // if (key === "print-pv-fr") {
      //   await this.printClassPv(this.selectedTrimester, "fr");
      //   return;
      // }

      if (!this.selectedTrimester) {
        this.$message.warning(
          this.$t("bulletin.selectStudentAndTrimester") ||
            "Please select a trimester"
        );
        return;
      }

      const [action, scope, language] = key.split("-");
      
      if (scope === "single") {
        // Print single student
        if (!this.selectedStudent) {
          this.$message.warning(
            this.$t("bulletin.selectStudentAndTrimester") ||
              "Please select a student"
          );
          return;
        }
        const lang = language === "tn" ? "ar" : "fr";
        await this.printStudentBulletin(
          this.selectedStudent,
          this.selectedTrimester,
          lang
        );
      } else if (scope === "all") {
        // Print all students
        if (!this.selectedClasse || !this.activeData || this.activeData.length === 0) {
          this.$message.warning(
            this.$t("error.erreur") || "No students available"
          );
          return;
        }
        const lang = language === "tn" ? "ar" : "fr";
        await this.printAllStudentsBulletin(lang);
      }
    },
    async printAllStudentsBulletin(language = "ar") {
      if (!this.selectedClasse || !this.selectedTrimester || !this.activeData || this.activeData.length === 0) {
        this.$message.warning(
          this.$t("error.erreur") || "No students available for printing"
        );
        return;
      }

      this.printLoading = true;
      try {
        // Fetch all students' data at once using the new endpoint
        const allData = await this.fetchAllStudentsMoyennes(
          this.selectedClasse,
          this.selectedTrimester
        );

        if (!allData || !allData.studentsData || allData.studentsData.length === 0) {
          this.$message.warning(
            this.$t("error.erreur") || "No data available for printing"
          );
          return;
        }

        // Generate PDFs for each student
        let doc = true;
        for (let i = 0; i < allData.studentsData.length; i++) {
          const studentData = allData.studentsData[i];
          
          // Transform data to match the format expected by generatePDF/generatePdfFr
          const transformedData = {
            bulletinConfig: allData.bulletinConfig,
            bulletinData: {
              ...studentData,
              numberOfStudents: allData.statistics.totalStudents,
              student: {
                ...studentData.student,
              },
            },
            rank: studentData.rank,
            averageMoyenne: allData.statistics.averageMoyenne,
            minMoyenne: allData.statistics.minMoyenne,
            maxMoyenne: allData.statistics.maxMoyenne,
            subjectsMinMaxAvg: allData.statistics.subjectsMinMaxAvg,
            numberOfStudents: allData.numberOfStudents,
          };

          // Generate PDF
          if (language === "ar") {
            doc = await this.generatePDF(transformedData, studentData.studentId, this.selectedTrimester, false, doc);
          } else {
            doc = await this.generatePdfFr(transformedData, studentData.studentId, this.selectedTrimester, false, doc);
          }

        }
        if (doc) {
          doc.save(`Bulletins_${language}.pdf`);
        }
        this.$message.success(
          this.$t("bulletin.printSuccess") || "All bulletins generated successfully"
        );
      } catch (error) {
        console.error("Error printing all bulletins:", error);
        this.$message.error(
          this.$t("error.erreur") || "Error generating bulletins"
        );
      } finally {
        this.printLoading = false;
      }
    },
    confirmeBulletinValidation() {
      const content = this.isValidatedBulletin()
        ? this.$t("bulletin.hideBulletin")
        : this.$t("bulletin.validateBulletin");
      this.$confirm({
        title: this.$t("all.sureTo"),
        content: (h) => <div style="color:red"> {content} </div>,
        onOk: () => {
          this.validateBulletin();
        },
        onCancel: () => {
          // User cancelled
        },
        class: "test",
      });
    },
    isValidatedBulletin() {
      if (this.selectedClasse) {
        const classroom = this.classes.find(
          (item) => item._id === this.selectedClasse
        );
        if (!classroom) return false;
        
        // Check for bulletinCollege field (for college bulletin)
        const bulletin = classroom.bulletinCollege || { sem1: false, sem2: false, sem3: false };
        const trimester = String(this.selectedTrimester);
        if (trimester === "1" && bulletin.sem1 === true) return true;
        else if (trimester === "2" && bulletin.sem2 === true)
          return true;
        else if (trimester === "3" && bulletin.sem3 === true)
          return true;
      }
      return false;
    },
    validateBulletin() {
      const isValidated = this.isValidatedBulletin();
      const classroom = this.classes.find(
        (item) => item._id === this.selectedClasse
      );
      if (!classroom) {
        this.$message.error(this.$t("error.erreur") || "Classroom not found");
        return;
      }
      
      this.validatingBulletin = true;
      
      const bulletin = classroom.bulletinCollege || { sem1: false, sem2: false, sem3: false };
      const trimester = String(this.selectedTrimester);
      if (trimester === "1") bulletin.sem1 = !bulletin.sem1;
      if (trimester === "2") bulletin.sem2 = !bulletin.sem2;
      if (trimester === "3") bulletin.sem3 = !bulletin.sem3;
      
      apiClient
        .patch("/classrooms/" + this.selectedClasse, {
          data: {
            bulletinCollege: bulletin,
          },
          bulletin: {
            classroom: this.selectedClasse,
            trimester: this.selectedTrimester,
            avaliable: !isValidated,
            type: "pedagogic",
          },
        })
        .then(() => {
          this.classes = this.classes.map((item) => {
            if (item._id === this.selectedClasse) item.bulletinCollege = bulletin;
            return item;
          });

          const className = classroom.name || this.$t("bulletin.classroom");
          if (!isValidated)
            this.$message.success(
              this.$t("bulletin.validated", { name: className })
            );
          else
            this.$message.success(
              this.$t("bulletin.unvalidated", { name: className })
            );
        })
        .catch((error) => {
          console.error("Error validating bulletin:", error);
          this.$message.error(
            this.$t("error.erreur") || "Error validating bulletin"
          );
        })
        .finally(() => {
          this.validatingBulletin = false;
        });
    },
  },
};
</script>


